import { postAdminLogin } from "../api/services/authApi";
import { getErrorMessage } from "../api/errorMessage";
import { getAdminCredentials } from "../config/staticAdminAuth";

/**
 * Pull bearer from common API response shapes.
 * @param {unknown} data
 * @returns {string}
 */
export function extractAuthToken(data) {
  if (!data || typeof data !== "object") return "";
  const d = data;
  const nested = d.data && typeof d.data === "object" ? d.data : null;
  return (
    (typeof d.token === "string" && d.token) ||
    (typeof d.accessToken === "string" && d.accessToken) ||
    (typeof d.access_token === "string" && d.access_token) ||
    (nested && typeof nested.token === "string" && nested.token) ||
    (nested && typeof nested.accessToken === "string" && nested.accessToken) ||
    (nested && typeof nested.access_token === "string" && nested.access_token) ||
    ""
  );
}

export const ADMIN_SIGN_IN_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Sanitize and validate sign-in fields. **Call this before any HTTP request.**
 *
 * @param {{ email?: string, password?: string }} input
 * @returns {{ ok: true, email: string, password: string } | { ok: false, fieldErrors: { email?: string, password?: string }, errorMessage: string }}
 */
export function validateAdminSignInInput(input) {
  const email = String(input?.email ?? "").trim();
  const passwordRaw = input?.password;
  const password =
    typeof passwordRaw === "string" ? passwordRaw : String(passwordRaw ?? "");

  /** @type {{ email?: string, password?: string }} */
  const fieldErrors = {};

  if (!email) {
    fieldErrors.email = "Email is required.";
  } else if (!ADMIN_SIGN_IN_EMAIL_RE.test(email)) {
    fieldErrors.email = "Enter a valid email address.";
  }

  if (!password) {
    fieldErrors.password = "Password is required.";
  } else if (password.length > 512) {
    fieldErrors.password = "Password is too long.";
  }

  if (Object.keys(fieldErrors).length > 0) {
    return {
      ok: false,
      fieldErrors,
      errorMessage:
        fieldErrors.email || fieldErrors.password || "Invalid input.",
    };
  }

  return { ok: true, email, password };
}

/**
 * Network + optional static fallback only. Expects **already validated** credentials
 * (use {@link validateAdminSignInInput} first).
 *
 * @param {{ email: string, password: string }} credentials
 * @returns {Promise<{ ok: true, token: string, usedFallback?: boolean } | { ok: false, errorMessage: string, usedFallback?: boolean }>}
 */
export async function completeAdminSignIn(credentials) {
  const { email, password } = credentials;

  try {
    const { data } = await postAdminLogin({ email, password });
    const token = extractAuthToken(data);
    if (!token) {
      return {
        ok: false,
        errorMessage:
          "Server signed you in but no token was returned. Check the /auth/login response shape.",
      };
    }
    return { ok: true, token };
  } catch (e) {
    const staticFallback =
      String(process.env.REACT_APP_ADMIN_LOGIN_STATIC_FALLBACK || "")
        .toLowerCase()
        .trim() === "true";

    if (staticFallback) {
      const { email: expectedEmail, password: expectedPassword } =
        getAdminCredentials();
      if (email === expectedEmail.trim() && password === expectedPassword) {
        const envToken = (process.env.REACT_APP_ADMIN_BEARER_TOKEN || "").trim();
        return {
          ok: true,
          token: envToken,
          usedFallback: true,
        };
      }
    }

    return { ok: false, errorMessage: getErrorMessage(e) };
  }
}

/**
 * Validates input, then calls the API (or static fallback). Prefer using
 * {@link validateAdminSignInInput} + {@link completeAdminSignIn} in the UI
 * so you can show validation errors before `setSubmitting(true)`.
 *
 * @param {{ email?: string, password?: string }} input
 */
export async function signInAdmin(input) {
  const v = validateAdminSignInInput(input);
  if (!v.ok) {
    return { ok: false, errorMessage: v.errorMessage };
  }
  return completeAdminSignIn({ email: v.email, password: v.password });
}
